(() => {
  // =====================
  // 活動設定
  // =====================
  const CAMPAIGN = {
    eligibleHandles: [
      "philips-ddl801",
      "philips-ddl709",
      "philips-ddl702-fvp",
      "philips-ddl702e",
      "philips-alpha-vp",
    ],
    giftHandle: "eog-u0001",
    giftQty: 1,
    minUnitPriceTWD: 20000, // 若你確定活動品永遠 >20000，可改成 0
    giftProperties: { _gwp: "philips-lock-gift-2026" },

    // session 標記：用來保險移除（避免 properties 遺失時贈品殘留）
    sessionFlagKey: "lj_gwp_added_eog_u0001",
  };

  // =====================
  // 工具
  // =====================
  const moneyCentsToTWD = (cents) => Math.round((cents || 0) / 100);

  let internalFetchDepth = 0;
  async function fetchJSON(url, options) {
    internalFetchDepth++;
    try {
      const res = await fetch(url, {
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        ...options,
      });
      if (!res.ok) throw new Error(`Request failed: ${url} (${res.status})`);
      return await res.json();
    } finally {
      internalFetchDepth--;
    }
  }

  async function getCart() {
    return fetchJSON("/cart.js", { method: "GET" });
  }

  async function getProductJSON(handle) {
    return fetchJSON(`/products/${handle}.js`, { method: "GET" });
  }

  function findGiftLine(cart) {
    return cart.items.find((it) => {
      const props = it.properties || {};
      return it.handle === CAMPAIGN.giftHandle && props._gwp === CAMPAIGN.giftProperties._gwp;
    });
  }

  function findGiftLineByHandle(cart) {
    // 保險用：properties 不見時，仍能找到該商品
    return cart.items.find((it) => it.handle === CAMPAIGN.giftHandle);
  }

  function cartHasEligible(cart) {
    return cart.items.some((it) => {
      if (!CAMPAIGN._eligibleVariantIds?.has(it.id)) return false;
      const unitPriceTWD = moneyCentsToTWD(it.price);
      return unitPriceTWD >= CAMPAIGN.minUnitPriceTWD;
    });
  }

  // =====================
  // 初始化快取：活動品 variantIds + 贈品 variantId
  // =====================
  async function initCampaignCache() {
    const cacheKey = "lj_gwp_cache_v2";
    const cached = sessionStorage.getItem(cacheKey);

    if (cached) {
      try {
        const obj = JSON.parse(cached);
        CAMPAIGN._eligibleVariantIds = new Set(obj.eligibleVariantIds || []);
        CAMPAIGN._giftVariantId = obj.giftVariantId;
        if (CAMPAIGN._eligibleVariantIds.size && CAMPAIGN._giftVariantId) return;
      } catch (_) {}
    }

    const eligibleVariantIds = new Set();
    for (const handle of CAMPAIGN.eligibleHandles) {
      const p = await getProductJSON(handle);
      (p?.variants || []).forEach((v) => eligibleVariantIds.add(v.id));
    }

    const gift = await getProductJSON(CAMPAIGN.giftHandle);
    const giftVariantId = gift?.variants?.[0]?.id;
    if (!giftVariantId) throw new Error("Gift product has no variants");

    CAMPAIGN._eligibleVariantIds = eligibleVariantIds;
    CAMPAIGN._giftVariantId = giftVariantId;

    sessionStorage.setItem(
      cacheKey,
      JSON.stringify({
        eligibleVariantIds: Array.from(eligibleVariantIds),
        giftVariantId,
      })
    );
  }

  const initPromise = initCampaignCache().catch(() => {});

  // =====================
  // 解析原始 body：要保留 sections / sections_url 等欄位
  // =====================
  function bodyToObject(body) {
    if (!body) return { obj: {}, type: "none" };

    // JSON string
    if (typeof body === "string") {
      try {
        const obj = JSON.parse(body);
        return { obj: obj && typeof obj === "object" ? obj : {}, type: "json" };
      } catch (_) {
        // urlencoded
        const params = new URLSearchParams(body);
        const obj = {};
        for (const [k, v] of params.entries()) obj[k] = v;
        return { obj, type: "urlencoded" };
      }
    }

    // FormData
    if (body instanceof FormData) {
      const obj = {};
      for (const [k, v] of body.entries()) obj[k] = v;
      return { obj, type: "formdata" };
    }

    // URLSearchParams
    if (body instanceof URLSearchParams) {
      const obj = {};
      for (const [k, v] of body.entries()) obj[k] = v;
      return { obj, type: "urlencoded" };
    }

    // 其他型態：不處理
    return { obj: {}, type: "unknown" };
  }

  function normalizeAddId(obj) {
    // Shopify 常見：id / quantity
    const id = obj.id ? Number(obj.id) : null;
    const quantity = obj.quantity ? Number(obj.quantity) : 1;
    return { id, quantity };
  }

  // =====================
  // 同步移除（刪電子鎖後要把贈品移掉）
  // =====================
  let syncing = false;
  let queued = false;

  async function syncGift() {
    if (syncing) {
      queued = true;
      return;
    }
    syncing = true;

    try {
      const cart = await getCart();
      const eligible = cartHasEligible(cart);

      if (eligible) {
        // 有活動品，確保贈品存在（若主題更新失敗/其他因素導致沒加到）
        const giftLine = findGiftLine(cart) || findGiftLineByHandle(cart);
        if (!giftLine) {
          await fetchJSON("/cart/add.js", {
            method: "POST",
            body: JSON.stringify({
              id: CAMPAIGN._giftVariantId,
              quantity: CAMPAIGN.giftQty,
              properties: CAMPAIGN.giftProperties,
            }),
          });
          sessionStorage.setItem(CAMPAIGN.sessionFlagKey, "1");
        }
      } else {
        // 無活動品：移除贈品
        const giftLineExact = findGiftLine(cart);
        if (giftLineExact) {
          await fetchJSON("/cart/change.js", {
            method: "POST",
            body: JSON.stringify({ id: giftLineExact.key, quantity: 0 }),
          });
          sessionStorage.removeItem(CAMPAIGN.sessionFlagKey);
        } else if (sessionStorage.getItem(CAMPAIGN.sessionFlagKey) === "1") {
          // properties 不見的保險路徑：只在「本 session 確定是我們加的」才刪
          const giftLineLoose = findGiftLineByHandle(cart);
          if (giftLineLoose) {
            await fetchJSON("/cart/change.js", {
              method: "POST",
              body: JSON.stringify({ id: giftLineLoose.key, quantity: 0 }),
            });
          }
          sessionStorage.removeItem(CAMPAIGN.sessionFlagKey);
        }
      }

      // 嘗試通知主題刷新（有些主題會聽這些事件）
      window.dispatchEvent(new CustomEvent("cart:refresh"));
      document.dispatchEvent(new CustomEvent("cart:refresh"));
      window.dispatchEvent(new CustomEvent("cart:updated"));
      document.dispatchEvent(new CustomEvent("cart:updated"));
    } catch (_) {
      // console.warn("[GWP] syncGift failed:", _);
    } finally {
      syncing = false;
      if (queued) {
        queued = false;
        syncGift();
      }
    }
  }

  // =====================
  // 攔截 fetch：保留原本 sections / sections_url
  // =====================
  const _fetch = window.fetch;
  window.fetch = async (...args) => {
    const [input, init] = args;
    const url = typeof input === "string" ? input : (input?.url || "");
    const method = (init?.method || "GET").toUpperCase();

    if (internalFetchDepth > 0) return _fetch(...args);

    const isCartAdd = method === "POST" && url.includes("/cart/add");
    const isCartMutate =
      method === "POST" &&
      (url.includes("/cart/change") || url.includes("/cart/update") || url.includes("/cart/clear"));

    if (isCartAdd) {
      await initPromise;

      const { obj } = bodyToObject(init?.body);
      const { id, quantity } = normalizeAddId(obj);

      // 若這次加入的是活動電子鎖 → 把贈品一起塞進同一次 add
      if (id && CAMPAIGN._eligibleVariantIds?.has(id)) {
        // 準備 items payload，並保留 sections / sections_url（主題更新抽屜需要）
        const payload = {
          ...obj, // 保留原本欄位（含 sections / sections_url / form_type 等）
          items: [
            // 先放原商品（保留原本可能帶的 properties）
            {
              id,
              quantity: quantity || 1,
              properties: obj.properties && typeof obj.properties === "object" ? obj.properties : undefined,
            },
            // 贈品
            {
              id: CAMPAIGN._giftVariantId,
              quantity: CAMPAIGN.giftQty,
              properties: CAMPAIGN.giftProperties,
            },
          ],
        };

        // 原本的單品欄位 id/quantity 會與 items 衝突，移掉避免歧義
        delete payload.id;
        delete payload.quantity;

        // 用原本的 URL（含 querystring），不要硬改 /cart/add.js
        const newInit = {
          ...init,
          headers: { ...(init?.headers || {}), "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify(payload),
        };

        const res = await _fetch(url.includes(".js") ? url : "/cart/add.js", newInit);

        // 記錄：本 session 我們確實自動加過贈品
        sessionStorage.setItem(CAMPAIGN.sessionFlagKey, "1");

        // 加完後再同步一次（保險：若不符門檻就移除）
        syncGift();
        return res;
      }

      // 非活動商品照舊，但仍同步（避免刪/改數量造成條件變化）
      const res = await _fetch(...args);
      syncGift();
      return res;
    }

    if (isCartMutate) {
      const res = await _fetch(...args);
      syncGift();
      return res;
    }

    return _fetch(...args);
  };

  // =====================
  // 攔截 XHR（有些流程/頁面仍會用）
  // =====================
  const _open = XMLHttpRequest.prototype.open;
  const _send = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this.__lj_url = url;
    this.__lj_method = (method || "GET").toUpperCase();
    return _open.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function (body) {
    const shouldSync =
      this.__lj_method === "POST" &&
      typeof this.__lj_url === "string" &&
      (this.__lj_url.includes("/cart/add") ||
        this.__lj_url.includes("/cart/change") ||
        this.__lj_url.includes("/cart/update") ||
        this.__lj_url.includes("/cart/clear"));

    if (shouldSync) {
      this.addEventListener("loadend", () => syncGift());
    }
    return _send.call(this, body);
  };

  // 初次載入同步一次（購物車頁面也靠這個做清理）
  document.addEventListener("DOMContentLoaded", async () => {
    await initPromise;
    syncGift();
  });
})();
